import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const getApiBase = () => {
  const envUrl = process.env.REACT_APP_BACKEND_URL;
  // Prefer the page's own origin when the bundled env URL points to a
  // different preview deployment — Cloudflare/ingress routes /api to the
  // same backend regardless of subdomain, so this avoids stale env URLs
  // breaking the app in preview while still working in production.
  if (envUrl) {
    try {
      const envHost = new URL(envUrl).hostname;
      if (envHost !== window.location.hostname) {
        return window.location.origin;
      }
    } catch { /* malformed env, ignore */ }
  }
  return envUrl || window.location.origin;
};

const API = getApiBase();

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// Helper to format API errors
const formatApiError = (detail) => {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).filter(Boolean).join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null = checking, false = not authenticated, object = authenticated
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/auth/me`, { withCredentials: true });
      setUser(res.data);
    } catch (err) {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    try {
      const res = await axios.post(`${API}/api/auth/login`, { email, password }, { withCredentials: true });
      setUser(res.data);
      return { success: true, user: res.data };
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 401) return { success: false, error: 'Invalid email or password' };
      if (status === 429) return { success: false, error: 'Too many attempts. Please wait a few minutes.' };
      if (!err.response) return { success: false, error: 'Cannot reach server. Check your connection.' };
      return { success: false, error: formatApiError(detail) || err.message };
    }
  };

  const register = async (data) => {
    try {
      const res = await axios.post(`${API}/api/auth/register`, data, { withCredentials: true });
      setUser(res.data);
      return { success: true, user: res.data };
    } catch (err) {
      return { success: false, error: formatApiError(err.response?.data?.detail) || err.message };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/api/auth/logout`, {}, { withCredentials: true });
    } catch {
    }
    setUser(false);
  };

  const forgotPassword = async (email) => {
    try {
      await axios.post(`${API}/api/auth/forgot-password`, { email });
      return { success: true };
    } catch (err) {
      return { success: false, error: formatApiError(err.response?.data?.detail) || err.message };
    }
  };

  const resetPassword = async (token, newPassword) => {
    try {
      await axios.post(`${API}/api/auth/reset-password`, { token, new_password: newPassword });
      return { success: true };
    } catch (err) {
      return { success: false, error: formatApiError(err.response?.data?.detail) || err.message };
    }
  };

  const refreshToken = async () => {
    try {
      await axios.post(`${API}/api/auth/refresh`, {}, { withCredentials: true });
      return true;
    } catch (err) {
      return false;
    }
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    forgotPassword,
    resetPassword,
    refreshToken,
    checkAuth,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    isClient: user?.role === 'client',
    isStaff: user && ['admin', 'sales_agent', 'logistics_manager', 'operations'].includes(user.role)
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
